import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { asObjectId } from '../../utils/references';
import { seedRcPro } from './reviseSeed';
import { defaultEffectiveDate } from './offerDefaults';
import { useTenantPaymentTerms } from '../../utils/productCatalog';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

let rowIdCounter = 0;
const newRowId = () => `row_${++rowIdCounter}_${Date.now()}`;

const DEFAULT_POLICYHOLDER = {
  companyName: '',
  address: '',
  canton: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

// Activity is captured as a PERCENTAGE (100 = full-time) because that is the
// unit underwriters and brokers actually use. The engine's canonical unit is
// the 0-1 decimal `activityFTE`; the conversion happens once, at the payload
// boundary, so the form never asks anyone to reason in fractions of a post.
export const PERCENT_FULL_TIME = 100;

const defaultPersonnelRow = () => ({
  id: newRowId(),
  // Optional; carried through to the proposal so a multi-doctor practice can
  // be listed by name. Never reaches the rating engine.
  personName: '',
  specialization: '',
  activityPercent: String(PERCENT_FULL_TIME),
  customLabel: '',
  customRate: '',
  isAssistant: false,
  // References the id of another personnel row (holding doctor). Resolved to
  // an index at submit time.
  holdingRowId: '',
  nonIndicatedOver10Pct: false,
});

const useRcProOfferFunctions = ({ onCreated, onClose, reviseFrom = null, editDraft = null }) => {
  // Revise mode: seed every field — including the personnel roster — from
  // the offer being revised so nothing has to be re-entered.
  // Seeded from the offer being REVISED (a new version of an issued
  // proposal) or the DRAFT being edited in place. Same reconstruction
  // either way — the difference is only what the save call does, so the
  // two flows cannot drift in what they carry over.
  const seedSource = reviseFrom || editDraft;
  const seed = seedSource ? seedRcPro(seedSource) : null;
  const [rateTable, setRateTable]               = useState(null);
  const [rateTableLoading, setRateTableLoading] = useState(true);
  const [rateTableError, setRateTableError]     = useState('');

  const [policyholder, setPolicyholder] = useState(() => seed?.policyholder || DEFAULT_POLICYHOLDER);
  const [clientId, setClientId] = useState(() => seed?.clientId || '');
  const [brokerId, setBrokerId] = useState(() => seed?.brokerId || '');
  const [productConfig, setProductConfig] = useState(() => ({
    sumInsured: '',
    discountPercent: '0',
    effectiveDate: defaultEffectiveDate(),
    ...(seed?.productConfig || {}),
  }));
  const [personnel, setPersonnel] = useState(() => seed?.personnel || [defaultPersonnelRow()]);
  // Read by updatePersonnel to answer "is this a sole practitioner" without
  // doing that work inside a state updater.
  const personnelRef = useRef(personnel);
  useEffect(() => { personnelRef.current = personnel; }, [personnel]);

  // RC Pro now carries a payment frequency (Yann 2026-08-28). Resolved from
  // the tenant's RC_PRO terms, exactly as PGM resolves its own — no product
  // conditional and no tenant branch, just the shared resolver.
  const paymentTerms = useTenantPaymentTerms('RC_PRO');

  const [calc, setCalc]                 = useState(null);
  const [calcLoading, setCalcLoading]   = useState(false);
  const [calcError, setCalcError]       = useState('');

  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');

  const debounceRef = useRef(null);
  const autoFilledRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/offers/rcpro/rate-table`, { withCredentials: true });
        if (cancelled) return;
        const data = resp.data?.data;
        setRateTable(data);
        setProductConfig(prev => ({
          ...prev,
          sumInsured: prev.sumInsured || String(data?.availableSumsInsured?.[0] ?? ''),
        }));
      } catch (err) {
        if (!cancelled) setRateTableError(err.response?.data?.message || 'Failed to load RC Pro rate table');
      } finally {
        if (!cancelled) setRateTableLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Specialization options sorted alphabetically by label. CUSTOM appended.
  // Also surface the nonIndicatedSurchargeEligible flag so the form knows
  // whether to show the surcharge checkbox on a row.
  const specializationOptions = useMemo(() => {
    const list = (rateTable?.specializations || [])
      .map(s => ({
        value: s.code,
        label: s.label,
        nogaCode: s.nogaCode,
        nonIndicatedSurchargeEligible: !!s.nonIndicatedSurchargeEligible,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    list.push({ value: 'CUSTOM', label: 'Custom / Autre', nogaCode: null, nonIndicatedSurchargeEligible: false });
    return list;
  }, [rateTable]);

  const specByCode = useMemo(
    () => Object.fromEntries((rateTable?.specializations || []).map(s => [s.code, s])),
    [rateTable],
  );

  const updatePolicyholder = useCallback((field, value) => {
    autoFilledRef.current.delete(field);
    setPolicyholder(prev => ({ ...prev, [field]: value }));
  }, []);

  const selectClient = useCallback((client) => {
    if (!client) { setClientId(''); return; }
    setClientId(client._id);
    setPolicyholder(prev => {
      const next = { ...prev };
      const newlyFilled = new Set();
      const trySet = (field, value) => {
        const wasAuto  = autoFilledRef.current.has(field);
        const canWrite = !prev[field] || wasAuto;
        if (!canWrite) return;
        if (!value) { if (wasAuto) next[field] = ''; return; }
        next[field] = value;
        newlyFilled.add(field);
      };
      trySet('companyName', client.companyName || client.displayName || '');
      const addr = client.address || {};
      const addrLine = [addr.street, addr.postalCode, addr.city, addr.country]
        .map(s => (s || '').trim()).filter(Boolean).join(', ');
      trySet('address', addrLine);
      const contactName = client.contactPerson
        || [client.firstName, client.lastName].filter(Boolean).join(' ');
      trySet('contactName',  contactName);
      trySet('contactEmail', client.email);
      trySet('contactPhone', client.phone);
      autoFilledRef.current = newlyFilled;
      return next;
    });
  }, []);
  const clearClient = useCallback(() => setClientId(''), []);

  const updateProductConfig = useCallback((field, value) =>
    setProductConfig(prev => ({ ...prev, [field]: value })),
  []);

  /**
   * A sole practitioner is their own preneur d'assurance.
   *
   * The doctor's name was being typed twice — once on the personnel row, once
   * in the policyholder block — and Samire reported the predictable outcome:
   * the person keying the quote put THEIR OWN name in the second field, and it
   * printed on the proposal as the policyholder.
   *
   * Mirrored into `contactName` and deliberately NOT `companyName`. The
   * document derives personName from policyholder.contactName, while a
   * populated companyName with no person name flips resolvePolicyholderParties
   * to isCompany — printing a sole doctor as though they were a practice. That
   * person-vs-company confusion is a defect this codebase has already had to
   * fix once; writing the name into the company field would reintroduce it.
   *
   * Same autoFilledRef contract selectClient uses: fill only while the field is
   * empty or still holds what we last auto-filled, so anything the user typed
   * is never overwritten, and clearing the roster name clears the mirror.
   * Only for a single-row roster — with several doctors named, which one is the
   * policyholder is a real question and not ours to guess.
   */
  const mirrorSolePractitionerName = useCallback((rows, name) => {
    if (!Array.isArray(rows) || rows.length !== 1) return;
    setPolicyholder(prev => {
      const wasAuto = autoFilledRef.current.has('contactName');
      if (prev.contactName && !wasAuto) return prev;
      const v = String(name || '').trim();
      if (!v) {
        if (!wasAuto) return prev;
        autoFilledRef.current.delete('contactName');
        return { ...prev, contactName: '' };
      }
      autoFilledRef.current.add('contactName');
      return { ...prev, contactName: v };
    });
  }, []);

  const updatePersonnel = useCallback((id, field, value) => {
    setPersonnel(prev => prev.map(p => {
      if (p.id !== id) return p;
      const next = { ...p, [field]: value };
      // Toggling the assistant flag clears fields that don't apply in the
      // opposite mode; keeps validation cleanly aligned with UI intent.
      if (field === 'isAssistant') {
        if (value) {
          next.specialization = '';
          next.customLabel = '';
          next.customRate = '';
          next.nonIndicatedOver10Pct = false;
        } else {
          next.holdingRowId = '';
        }
      }
      // Changing specialization on a non-assistant row: drop the surcharge flag
      // if the new specialty is not eligible.
      if (field === 'specialization' && !next.isAssistant) {
        const spec = specByCode[value];
        if (!spec?.nonIndicatedSurchargeEligible) next.nonIndicatedOver10Pct = false;
      }
      return next;
    }));
    // Kept OUT of the setPersonnel updater: a state updater must stay pure, and
    // React may invoke it twice. A personName edit never changes the row count,
    // so the pre-update roster answers "is this a sole practitioner".
    if (field === 'personName') mirrorSolePractitionerName(personnelRef.current, value);
  }, [specByCode, mirrorSolePractitionerName]);

  const addPersonnel = useCallback(() => {
    setPersonnel(prev => [...prev, defaultPersonnelRow()]);
  }, []);

  const removePersonnel = useCallback((id) => {
    setPersonnel(prev => {
      if (prev.length <= 1) return prev;
      const filtered = prev.filter(p => p.id !== id);
      // Any assistant that was pointing at the removed row loses its link.
      return filtered.map(p => (p.holdingRowId === id ? { ...p, holdingRowId: '' } : p));
    });
  }, []);

  // Convert holdingRowId (client-side stable id) → holdingRowIndex (server
  // expects position in personnel array).
  const buildCalcPayload = useCallback(() => ({
    personnel: personnel.map((p) => {
      const holdingRowIndex = p.isAssistant
        ? personnel.findIndex(x => x.id === p.holdingRowId)
        : null;
      return {
        personName: (p.personName || '').trim(),
        specialization: p.specialization,
        // Sent as the NAMED percentage unit. A bare number in an FTE field
        // is what let D-16 through; `activityPercent: 30` cannot be
        // misread, and the server normalises it to the stored decimal.
        activityPercent: Number(p.activityPercent),
        customLabel: p.specialization === 'CUSTOM' ? p.customLabel : undefined,
        customRate:  p.specialization === 'CUSTOM' ? Number(p.customRate) : undefined,
        isAssistant: !!p.isAssistant,
        holdingRowIndex,
        nonIndicatedOver10Pct: !!p.nonIndicatedOver10Pct,
      };
    }),
    sumInsured: Number(productConfig.sumInsured),
    discountPercent: Number(productConfig.discountPercent || 0),
  }), [personnel, productConfig.sumInsured, productConfig.discountPercent]);

  const missingFields = useMemo(() => {
    const miss = [];
    if (!productConfig.sumInsured) miss.push('sumInsured');
    personnel.forEach((p, i) => {
      const label = `personnel[${i + 1}]`;
      const pct = Number(p.activityPercent);
      if (!p.activityPercent || !Number.isFinite(pct) || pct <= 0 || pct > PERCENT_FULL_TIME) {
        miss.push(`${label}.activityPercent`);
      }
      if (p.isAssistant) {
        if (!p.holdingRowId) miss.push(`${label}.holdingDoctor`);
        // If the referenced holding row was later marked as an assistant,
        // catch it here rather than surprising the server.
        const holding = personnel.find(x => x.id === p.holdingRowId);
        if (holding && holding.isAssistant) miss.push(`${label}.holdingDoctor(mustBeDoctor)`);
      } else {
        if (!p.specialization) miss.push(`${label}.specialization`);
        if (p.specialization === 'CUSTOM') {
          if (!p.customLabel || !p.customLabel.trim()) miss.push(`${label}.customLabel`);
          if (!p.customRate  || Number(p.customRate) <= 0) miss.push(`${label}.customRate`);
        }
      }
    });
    return miss;
  }, [productConfig.sumInsured, personnel]);
  const canCalculate = missingFields.length === 0;

  const runCalculate = useCallback(async () => {
    if (!canCalculate) {
      setCalc(null);
      setCalcError('');
      return;
    }
    setCalcLoading(true);
    setCalcError('');
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/rcpro/calculate`,
        buildCalcPayload(),
        { withCredentials: true },
      );
      setCalc(resp.data?.data);
    } catch (err) {
      setCalcError(err.response?.data?.message || 'Could not calculate premium');
      setCalc(null);
    } finally {
      setCalcLoading(false);
    }
  }, [canCalculate, buildCalcPayload]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runCalculate, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [runCalculate]);

  const submit = useCallback(async (status = 'sent') => {
    setSaving(true);
    setSaveError('');
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/rcpro`,
        {
          policyholder,
          clientId: asObjectId(clientId) || undefined,
          // Only a real id is ever posted as a reference — never a display value.
          ...(asObjectId(brokerId) && { broker: asObjectId(brokerId) }),
          productConfig: {
            ...buildCalcPayload(),
            effectiveDate: productConfig.effectiveDate,
            paymentFrequency: productConfig.paymentFrequency || paymentTerms.frequency,
          },
          status,
          ...(reviseFrom?._id && { revisionOf: reviseFrom._id }),
          // Editing a draft returns to the SAME product endpoint with the
          // draft's id, so the premium and the params snapshot are rebuilt
          // by the identical code that built them the first time. The
          // server updates in place: same _id, same proposal number.
          ...(editDraft?._id && { draftId: editDraft._id }),
        },
        { withCredentials: true },
      );
      onCreated && onCreated(resp.data?.data);
      onClose && onClose();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to create offer');
    } finally {
      setSaving(false);
    }
  }, [policyholder, clientId, brokerId, productConfig.effectiveDate, productConfig.paymentFrequency, paymentTerms.frequency, reviseFrom, editDraft, buildCalcPayload, onCreated, onClose]);

  return {
    rateTable, rateTableLoading, rateTableError,
    specializationOptions,
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProductConfig,
    personnel, updatePersonnel, addPersonnel, removePersonnel,
    calc, calcLoading, calcError,
    paymentTerms,
    canCalculate, missingFields,
    saving, saveError,
    saveDraft: () => submit('draft'),
    createOffer: () => submit('sent'),
  };
};

export default useRcProOfferFunctions;
